import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { ChevronRight, Loader2 } from 'lucide-react';

import { usePromptRevisions } from '@codes/agents';
import { formatDate } from '@codes/utils';

import { PromptRevisionDetailSheet } from './PromptRevisionDetailSheet';

interface PromptRevisionListProps {
    promptId: string;
    currentPrompt?: PromptView | null;
    onSelectRevision?: (revision: PromptView) => void;
    selectedRevisionId?: string;
    className?: string;
}

const INIT_PAGE = 0;

export const PromptRevisionList: React.FC<PromptRevisionListProps> = ({
    promptId,
    currentPrompt,
    onSelectRevision,
    selectedRevisionId,
    className = '',
}) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(INIT_PAGE);
    const [allRevisions, setAllRevisions] = useState<PromptView[]>([]);
    const [selectedRevision, setSelectedRevision] = useState<PromptView | null>(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const {
        data: revisionsData,
        isLoading,
        error,
    } = usePromptRevisions({
        promptId,
        hidden: false,
        limit: 10,
        page: currentPage,
    });

    React.useEffect(() => {
        setCurrentPage(INIT_PAGE);
        setAllRevisions([]);
    }, [promptId]);

    React.useEffect(() => {
        if (revisionsData?.data) {
            if (currentPage === INIT_PAGE) {
                setAllRevisions(revisionsData.data);
            } else {
                setAllRevisions(prev => [...prev, ...revisionsData.data]);
            }
        }
    }, [revisionsData, currentPage]);

    const hasMoreItems = revisionsData ? revisionsData.total > allRevisions.length : false;

    const handleRevisionClick = (revision: PromptView) => {
        setSelectedRevision(revision);
        setIsSheetOpen(true);
    };

    const handleApplyRevision = (revision: PromptView) => {
        onSelectRevision?.(revision);
    };

    if (isLoading && allRevisions.length === 0) {
        return (
            <div className={`flex items-center justify-center p-4 ${className}`}>
                <Loader2 className="h-5 w-5 animate-spin text-text-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`text-sm text-red-500 p-4 ${className}`}>
                {t('prompt.form.revisions.error', '수정 내역을 불러오지 못했습니다.')}
            </div>
        );
    }

    if (allRevisions.length === 0 && !isLoading) {
        return null;
    }

    return (
        <div className={`mt-6 ${className}`}>
            <div className="text-sm font-medium text-text mb-4">{t('prompt.form.revisions.title', '수정 내역')}</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {allRevisions.map(revision => (
                    <div
                        key={revision.id}
                        className={`
                            flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors
                            ${
                                selectedRevisionId === revision.id
                                    ? 'bg-blue-50 border border-blue-200'
                                    : 'bg-box hover:bg-box'
                            }
                        `}
                        onClick={() => handleRevisionClick(revision)}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-text-500">
                                    {t('prompt.form.revisions.modifiedDate', '수정일')}
                                </span>
                                <span className="text-xs text-text-400">•</span>
                                <span className="text-xs text-text-500">
                                    {revision.createdAt
                                        ? formatDate(revision.createdAt)
                                        : t('prompt.form.revisions.unknownDate', '알 수 없는 날짜')}
                                </span>
                            </div>

                            {revision.contents && revision.contents.length > 0 && (
                                <div className="text-sm text-sub-title truncate">
                                    {revision.contents[0].length > 80
                                        ? `${revision.contents[0].substring(0, 80)}...`
                                        : revision.contents[0]}
                                </div>
                            )}
                        </div>

                        <ChevronRight className="w-4 h-4 text-text-400 flex-shrink-0 ml-2" />
                    </div>
                ))}
            </div>

            {hasMoreItems && (
                <div className="flex justify-center mt-3">
                    <button
                        type="button"
                        className="text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                        ) : (
                            t('prompt.form.revisions.viewMore', 'View More')
                        )}
                    </button>
                </div>
            )}

            <PromptRevisionDetailSheet
                revision={selectedRevision}
                currentPrompt={currentPrompt}
                isOpen={isSheetOpen}
                onOpenChange={setIsSheetOpen}
                onApplyRevision={handleApplyRevision}
                onDeleteRevision={deletedRevision => {
                    setAllRevisions(prev => prev.filter(r => r.id !== deletedRevision.id));
                    // 만약 삭제된 revision이 현재 선택된 revision이면 선택 해제
                    if (selectedRevision?.id === deletedRevision.id) {
                        setSelectedRevision(null);
                    }
                }}
                onCreateRevision={newRevision => {
                    setAllRevisions(prev => [newRevision, ...prev]);
                }}
            />
        </div>
    );
};
